import FileManager from "../utils/file-manager";
import React, { useState, useEffect, useCallback, useRef } from "react";
import Editor from "@monaco-editor/react";
import { ReactComponent as ExpandIcon } from '../../assets/logos/expand.svg';
import { ReactComponent as CollapseIcon } from '../../assets/logos/collapse.svg';
import { ReactComponent as BulbIcon } from '../../assets/logos/bulb.svg';
import { ReactComponent as OptionIcon } from '../../assets/logos/options.svg';
import {
  Button,
  Space,
  message,
  Spin,
  Progress,
  Modal,
  Tooltip,
  Badge,
  Alert,
  Skeleton,
  Tabs,
  notification
} from "antd";

import PluginsLoader from '../common/plugins-loader';
import CreateDbModal from "./databaseConn/createDb";
import ShowDbConnection from "./databaseConn/ShowDbConnection";

import {
  BulbOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  LoadingOutlined,
  DatabaseOutlined
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import {
  saveFileInIde,
  getFileContent,
  getUrlToRunCode,
  getIdeProjectDetails,
  getProgrammingTip,
  clearVncConf,
  installPackageInVnc,
  generateSqlpadDatabaseConnection,
  getSqlpadDatabaseConnection
} from "../../services/ide-apis";
import {
  getFileExtension,
  trackEvent,
  getCssClassNameForAutoSize,
  isMobile,
} from "../utils/util-functions";
import { global_var } from "../../services/cs-lab-config";
import ReactMarkdown from "react-markdown";
import IdeHeader from "./ide-header";
import { buildFileContentMap, isProjectEmptyFromMap, hasRealCode } from "./ide-empty-check";
import CreateIDEProjectModal from "./create-ide-project-modal";
import { ReactComponent as ComplexityIcon } from '../../assets/logos/complexity.svg';
import ComplexityModal from "./complexity/complexity-analysis-modal";
import InlineAiPrompt from "./inlineAiPrompt/inline-ai-prompt"
import './python-ide.css'
import AiChatBot from "./aiChatBot/ai-chat-bot"

const PythonIDE = () => {
  document.title = "CS Lab | Python IDE";

  let { ideProjectId } = useParams();

  let hideHeader = new URLSearchParams(window.location.search).get(
    "hideHeader"
  );

  let isSaveDisabledInIDE = new URLSearchParams(window.location.search).get(
    "isSaveDisabledInIDE"
  );

  let isEditAndSaveAllowedInIDE = new URLSearchParams(window.location.search).get(
    "isEditAndSaveAllowedInIDE"
  );

  const [selectedTheme, setSelectedTheme] = useState("light");
  const [ideProjectDetails, setIdeProjectDetails] = useState(undefined);
  const updateProjectDetails = useCallback((updatedProject) => {
    setIdeProjectDetails({ ...updatedProject });
  }, []);

  const [selectedFile, setSelectedFile] = useState(undefined);
  const [inputCode, setInputCode] = useState("");
  const [vncServerUrl, setVncServerUrl] = useState(undefined);

  const [readOnlyMode, setReadOnlyMode] = useState(false);

  const [showSkulptWindow, setShowSkulptWindow] = useState(false);
  const [skulptOutputData, setSkulptOutputData] = useState("");
  const [codeOutputError, setCodeOutputError] = useState({});
  const [skulptInputData, setSkulptInputData] = useState("");
  const [showSkulptInputField, setShowSkulptInputField] = useState(false);

  const [outputWindowExpanded, setOutputWindowExpanded] = useState(false);
  const [fileManagerExpanded, setFileManagerExpanded] = useState(true);

  const [newProjectModal, setNewProjectModal] = useState(false);
  const [mobileFileManagerModal, setMobileFileManagerModal] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);

  const [fileLoadingInProgress, setFileLoadingInProgress] = useState(false);
  const [fileSavingInProgress, setFileSavingInProgress] = useState(false);
  const [vncRunInProgress, setVncRunInProgress] = useState(false);
  const [vncRestartInProgress, setVncRestartInProgress] = useState(false);
  const [codeExecutionInProgress, setCodeExecutionInProgress] = useState(false);

  const [machineBootingProgress, setMachineBootingProgress] = useState(0);
  const [unsavedChangePresent, setUnsavedChangePresent] = useState(false);

  const [programmingTip, setProgrammingTip] = useState("");

  const [fileMetaList, setFileMetaList] = useState([]);
  const [fileHasContentMap, setFileHasContentMap] = useState({});

  const [selectedPackagesList, setSelectedPackagesList] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(null);
  const [editorInstance, setEditorInstance] = useState(null);
  const monacoRef = useRef(null);
  const [showInlineAiPrompt, setShowInlineAiPrompt] = useState(false);
  const [tabActiveKey, setTabActiveKey] = useState('Output');
  const [enabledAIfeatures, setEnabledAIfeatures] = useState([]);

  const autoSaveTimerRef = useRef(null);
  const outputRef = useRef(null);
  const workerRef = useRef(null);
  const [showDatabaseConnectBtn, setShowDatabaseConnectBtn] = useState(false);
  const [isCreateDbModalVisible, setIsCreateDbModalVisible] = useState(false);
  const [isShowDbConnectionModalVisible, setIsShowDbConnectionModalVisible] = useState(false);
  const [connectionText, setConnectionText] = useState('');
  const [isSaveDisable, setIsSaveDisable] = useState(false);
  
  useEffect(() => {
  if (isSaveDisabledInIDE === "true") {
      setIsSaveDisable(true);
    } else {
      setIsSaveDisable(false);
    }
  }, [isSaveDisabledInIDE]);


  const tabs = ['Output']
  if (global_var.role_id !== global_var.STUDENT_ROLE_ID || enabledAIfeatures.length) {
    tabs.push('Ai Chatbot')
  }

  useEffect(() => {
    // If the output element exists, scroll to the bottom
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [skulptOutputData]); // This dependency ensures the effect runs only when output changes


  const handleSelectedTheme = () => {
    const themeName = selectedTheme === 'light' ? 'vs-dark' : 'light';

    trackEvent("ide_dark_mode_toggle_clicked", {
      ide_project_stack_name: "python",
      ide_theme_value: themeName,
    });

    setSelectedTheme(themeName);
  }

  const handleCodeChange = (value) => {
    setInputCode(value);
    setUnsavedChangePresent(true);
    setFileHasContentMap((prev) => ({
      ...prev,
      [selectedFile.id]: hasRealCode(value, selectedFile.fileName),
    }));
    if (editorInstance) {
      var position = editorInstance.getPosition()
      setCursorPosition(position);
    }

    if (isSaveDisable) {
      return
    };

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveFile(value);
    }, 2000);
  };

  function toggleOutputWindow() {
    setOutputWindowExpanded(!outputWindowExpanded);
  }

  const navigateToFile = useCallback(
    (event) => {
      if (!ideProjectId) {
        return;
      }

      if (event.node.fileMimeType === "image") {
        setOutputWindowExpanded(false);
        setSelectedFile(event.node);
        setUnsavedChangePresent(false);
      } else {
        setSelectedFile(undefined);
        setFileLoadingInProgress(true);

        getFileContent(ideProjectId, event.node.id).then((response) => {
          setFileLoadingInProgress(false);

          response = response.data;

          if (response.statusCode === 200) {
            event.node.fileContent = response.fileContent;
            setInputCode(event.node.fileContent);
            setSelectedFile(event.node);
            setUnsavedChangePresent(false);
          } else {
            message.error(response.message);
          }
        });
      }
    },
    [ideProjectId]
  );

  const nodeSelected = useCallback(
    (nodeIds, event) => {
      // The following function will handle file opening

      if (!event.node.isFile) {
        return;
      }

      if (unsavedChangePresent) {
        Modal.confirm({
          title: "There are unsaved changes in your editor",
          cancelText: "Leave editor",
          okText: "Back to editor",
          onCancel() {
            navigateToFile(event);
          },
        });
        return;
      }

      navigateToFile(event);
    },
    [unsavedChangePresent, navigateToFile]
  );

  const handleNewFile = useCallback(
    (fileMetaListObj, updatedTreeData) => {
      if (!fileMetaListObj.isFile) {
        return;
      }

      setFileMetaList(updatedTreeData);
      setFileHasContentMap((prev) => ({ ...prev, [fileMetaListObj.id]: false }));

      navigateToFile({ node: fileMetaListObj });
    },
    [navigateToFile]
  );

  const handleFileDeleted = useCallback(
    (deletedNode, updatedTreeData) => {
      setFileMetaList(updatedTreeData);
      setFileHasContentMap((prev) => {
        const newMap = { ...prev };
        delete newMap[deletedNode.id];
        return newMap;
      });

      if (deletedNode.id === (selectedFile ? selectedFile.id : false)) {
        setSelectedFile(undefined);
      }
    },
    [selectedFile, setSelectedFile]
  );

  const handleFileRenamed = useCallback(
    (newTreeData, newFileMetadata) => {
      if (newFileMetadata.id === (selectedFile ? selectedFile.id : false)) {
        setSelectedFile((oldFileObj) => {
          const newObj = {
            ...oldFileObj,
            fileName: newFileMetadata.fileMetaListObj.fileName,
            fileMimeType: newFileMetadata.fileMetaListObj.fileMimeType,
          };
          if (newFileMetadata.fileMetaListObj.fileS3reference) {
            newObj.fileS3reference =
              newFileMetadata.fileMetaListObj.fileS3reference;
          }
          return newObj;
        });
        setFileHasContentMap((prev) => ({
          ...prev,
          [newFileMetadata.id]: hasRealCode(inputCode, newFileMetadata.fileMetaListObj.fileName),
        }));
      }
    },
    [selectedFile, setSelectedFile, inputCode]
  );

  let intervalId;
  function startProgressBar() {
    const tipsToChangePercent = [4, 26, 56, 86];
    setMachineBootingProgress(0);

    intervalId = setInterval(() => {
      setMachineBootingProgress((prevProgress) => {
        if (prevProgress >= 100) {
          clearInterval(intervalId);
          return 100;
        }

        if (tipsToChangePercent.indexOf(prevProgress) !== -1) {
          getProgrammingTip("python").then((response) => {
            response = response.data;
            if (response.statusCode === 200) {
              setProgrammingTip(response.text);
            }
          });
        }

        return prevProgress + 1;
      });
    }, 600); // Update the progress every 600ms (adjust as needed)
  }

  const codeSnipitsForVNC = [
    "import turtle",
    "from turtle",
    "import tkinter",
    "from tkinter",
    "import matplotlib",
    "from matplotlib",
    "import pandas",
    "import numpy",
    "from pandas",
    "from numpy",
    "import pygame",
    "from pygame",
    "import forex_python",
    "from forex_python",
    "import transliterate",
    "from transliterate",
    "import tensorflow",
    "from tensorflow",
    "import indic_transliteration",
    "from indic_transliteration",
    "from sklearn",
    "import sklearn",
    "import mysql.connector",
    "import cv2",
    "from cv2",
    "import wikipedia",
    "from wikipedia",
  ];
  async function handleRunCode() {
    const fileExt = getFileExtension(selectedFile.fileName);

    if (fileExt !== "py") {
      message.error("You can only run .py files");
      return;
    }

    if (unsavedChangePresent) {
      trackEvent("ide_run_clicked_without_save", {
        ide_project_stack_name: "python",
      });
      await saveFileInIdeHandler();
    }

    trackEvent("ide_run_button_clicked", {
      ide_project_stack_name: "python",
    });

    var runInVNC = codeSnipitsForVNC.some(function (element) {
      return inputCode.includes(element);
    });

    if (runInVNC || selectedPackagesList.length) {
      runCodeInVNC();
    }
    else {
      runCodeInSkulpt();
    }
  }

  function runCodeInVNC() {
    // The following function will handle code execution in VNC

    setShowSkulptWindow(false);
    setVncRunInProgress(true);
    startProgressBar();
    setOutputWindowExpanded(true);
    setTabActiveKey('Output')

    let executionFilename =
      (selectedFile.path ? selectedFile.path.replace("/", "") + "/" : "") +
      selectedFile.fileName;
    let additionalParams = {}
    if (selectedPackagesList.length) {
      additionalParams.packageList = selectedPackagesList;
    }

    trackEvent("ide_run_in_vnc", {
      ide_project_stack_name: "python",
    });

    getUrlToRunCode(ideProjectId, executionFilename, additionalParams).then((response) => {

      response = response.data;

      if (response.statusCode === 200) {
        setVncServerUrl(response.vncServerUrl + "&random=" + (new Date()).getTime() + Math.floor(Math.random() * 1000000));
        setVncRunInProgress(false);
        clearInterval(intervalId);
      } else if (response.statusCode === 202) {

        // Status is 202. Wait 30 seconds, then start polling.
        setTimeout(() => {

          // This ID is for the new polling interval
          const pollingIntervalId = setInterval(() => {

            getUrlToRunCode(ideProjectId, executionFilename, additionalParams).then((pollingResponse) => {

              pollingResponse = pollingResponse.data;

              if (pollingResponse.statusCode === 200) {
                // Success! Stop polling and clear both intervals.
                setVncServerUrl(pollingResponse.vncServerUrl + "&random=" + (new Date()).getTime() + Math.floor(Math.random() * 1000000));
                setVncRunInProgress(false);

                clearInterval(pollingIntervalId); // <-- Clear the new polling interval
                clearInterval(intervalId);      // <-- Clear the original outer interval

              } else if (pollingResponse.statusCode === 202) {
                // Still processing, do nothing and let the interval run again
                console.log("Polling: Still 202, waiting...");
              } else {
                // Polling returned an error
                message.error(pollingResponse.message);
                setOutputWindowExpanded(false);
                setVncRunInProgress(false);

                clearInterval(pollingIntervalId); // <-- Stop polling on error
                clearInterval(intervalId);
              }
            }).catch((err) => {
              // Handle network errors during polling
              message.error("Polling request failed: " + err.message);
              setVncRunInProgress(false);
              clearInterval(pollingIntervalId);
              clearInterval(intervalId);
            });
          }, 5000); // <-- This is your 5-second polling frequency

        }, 30000); // <-- This is the 30-second (30,000ms) initial delay

      } else {
        message.error(response.message);
        setOutputWindowExpanded(false);
        setVncRunInProgress(false);
        clearInterval(intervalId);
      }
    });
  }

  const updateFileTreeWithContent = (fileTree, fileMap) => {
    return fileTree.map(node => {
      // Create a copy of the node to avoid direct mutation
      const newNode = { ...node };

      // If this node is a file and we have new content for it, add it
      if (node.isFile && fileMap.has(node.fileName) && !node.fileContent) {
        newNode.fileContent = fileMap.get(node.fileName);
      }

      // If the node has children, recursively update them as well
      if (node.children) {
        newNode.children = updateFileTreeWithContent(node.children, fileMap);
      }

      return newNode;
    });
  };

  const buildFileMap = async (fileTree) => {
    const fileMap = new Map();
    const fetchPromises = [];

    // Recursive function to traverse the file tree
    function traverse(nodes) {
      nodes.forEach(node => {
        if (node.isFile) {
          if (node.fileContent) {
            // If content is already here, add it directly
            fileMap.set(node.fileName, node.fileContent);
          } else {
            // If content is missing, create a promise to fetch it
            const fetchPromise = getFileContent(ideProjectId, node.id)
              .then(response => {
                if (response.data.statusCode === 200) {
                  // When fetched, add it to the map
                  fileMap.set(node.fileName, response.data.fileContent);
                }
              });
            // Add this promise to our list of tasks
            fetchPromises.push(fetchPromise);
          }
        }
        if (node.children) {
          traverse(node.children);
        }
      });
    }

    traverse(fileTree);

    // CRITICAL STEP: Wait for all the fetch promises to complete
    await Promise.all(fetchPromises);

    return fileMap;
  };

  async function runCodeInSkulpt() {
    setShowSkulptWindow(true);
    setVncServerUrl(undefined);
    setOutputWindowExpanded(true);
    setSkulptOutputData("");
    setCodeOutputError({});
    setTabActiveKey('Output');
    setCodeExecutionInProgress(true);

    // 1. Ensure all files from the file tree are included.
    const fileMap = await buildFileMap(fileMetaList);

    // Update the main file list state with the newly fetched content
    const newFileTree = updateFileTreeWithContent(fileMetaList, fileMap);
    setFileMetaList(newFileTree);

    // 2. Ensure the currently open file (with any unsaved changes) is included.
    if (selectedFile) {
      fileMap.set(selectedFile.fileName, inputCode);
    }

    // 3. Convert Map to a plain object for postMessage.
    const files = Object.fromEntries(fileMap);

    // 4. Send the files and the entry point to the worker.
    workerRef.current.postMessage({
      type: 'run',
      data: {
        files: files,
        entryPoint: selectedFile.fileName
      }
    });

  }

  async function autoSaveFile(value) {

    saveFileInIde(ideProjectId, selectedFile.id, {
      fileContent: value,
      updateS3Content: true,
      fileOrFolderObject: {},
    }).then((response) => {
      response = response.data;

      if (response.statusCode === 200) {
        setUnsavedChangePresent(false);
        const newTree = updateFileContentInTree(fileMetaList, selectedFile.id, value);
        setFileMetaList(newTree);
        setFileHasContentMap((prev) => ({
          ...prev,
          [selectedFile.id]: hasRealCode(value, selectedFile.fileName),
        }));
      }
    }).catch(() => {
    });
  }

  const updateFileContentInTree = (fileTree, fileId, newContent) => {
    return fileTree.map(node => {
      if (node.id === fileId) {
        return { ...node, fileContent: newContent };
      }
      if (node.children) {
        return { ...node, children: updateFileContentInTree(node.children, fileId, newContent) };
      }
      return node;
    });
  };

  async function saveFileInIdeHandler(chatbotInput) {
    if (isSaveDisable) {
      return
    };
    if (chatbotInput) {
      trackEvent("chatbot_insert_button_clicked", {
        ide_project_stack_name: "python",
      });
    }
    setFileSavingInProgress(true);

    return saveFileInIde(ideProjectId, selectedFile.id, {
      fileContent: chatbotInput ? chatbotInput : inputCode,
      updateS3Content: true,
      fileOrFolderObject: {},
    }).then((response) => {
      setFileSavingInProgress(false);
      response = response.data;

      if (response.statusCode === 200) {

        // After a successful save, update the fileMetaList state with the new content.
        const newTree = updateFileContentInTree(fileMetaList, selectedFile.id, chatbotInput ? chatbotInput : inputCode);
        setFileMetaList(newTree);
        setFileHasContentMap((prev) => ({
          ...prev,
          [selectedFile.id]: hasRealCode(chatbotInput ? chatbotInput : inputCode, selectedFile.fileName),
        }));

        message.success(response.message);
        setUnsavedChangePresent(false);
      } else {
        message.error(response.message);
      }
      return response;
    });
  }

  function handleSaveFile() {
    // The following function will handle file saving

    if (!unsavedChangePresent) {
      message.info("There no changes to save");
      return;
    }

    trackEvent("ide_save_button_clicked", {
      ide_project_stack_name: "python",
    });

    saveFileInIdeHandler();
  }

  function getCssClassName(component) {
    return getCssClassNameForAutoSize(
      component,
      fileManagerExpanded,
      outputWindowExpanded
    );
  }


  function handleVncRestartClicked() {
    Modal.confirm({
      title: "Are you sure you want to restart your machine",
      content:
        "Only restart your code output device if you are experiencing problems obtaining your code output.",
      cancelText: "Cancel",
      okText: "Proceed",
      onOk() {

        trackEvent("ide_restart_btn_clicked", {
          ide_project_stack_name: "python",
        });

        setVncRestartInProgress(true);

        clearVncConf().then((response) => {
          setVncRestartInProgress(false);
          response = response.data;

          if (response.statusCode === 200) {
            handleRunCode();
          } else {
            message.error(response.message);
          }
        });
      },
    });
  }

  useEffect(() => {
    if (!ideProjectDetails) {
      return;
    }

    const findNode = (tData, fileId, dataObj) => {
      for (let i = 0; i < tData.length; i++) {
        const node = tData[i];
        if (node.id === fileId) {
          dataObj["node"] = node;
          return true;
        }
        if (node.children) {
          if (findNode(node.children, fileId, dataObj)) {
            return true;
          }
        }
      }
      return false;
    };

    if (ideProjectDetails.lastAccessedFileId) {
      const dataObj = {};

      findNode(
        ideProjectDetails.fileMetaList,
        ideProjectDetails.lastAccessedFileId,
        dataObj
      );

      if (dataObj.node) {
        navigateToFile({ node: dataObj.node });
      }
    }
  }, [ideProjectDetails, navigateToFile]);

  // Re-derived on every project change so that submitting for evaluation — which sets
  // editDisabled — locks the editor straight away rather than on the next load.
  useEffect(() => {
    if (!ideProjectDetails) {
      return;
    }
    setReadOnlyMode(
      ideProjectDetails.evaluationStatus === "Evaluated" ||
        ideProjectDetails.fkUserLoginId !== global_var.user_login_id ||
        !!ideProjectDetails.editDisabled
    );

    if (isEditAndSaveAllowedInIDE === 'true') {
      setReadOnlyMode(false);
    }

  }, [ideProjectDetails, isEditAndSaveAllowedInIDE]);

  useEffect(() => {
    if (ideProjectId) {
      setNewProjectModal(false);
      getIdeProjectDetails(ideProjectId).then((response) => {
        response = response.data;

        if (response.statusCode === 200) {
          
          let isOwner = response.projectDetails.fkUserLoginId === global_var.user_login_id
          let isProject = response?.projectDetails?.fkIdeProjectTypeName === "Project" || isEditAndSaveAllowedInIDE === "true";
          let isNotStudent = global_var.role_id !== global_var.STUDENT_ROLE_ID;
          let conditionObj = {
            isOwner,
            isProject,
            isNotStudent
          }

          let checkObj;

          if (isProject) {
              checkObj = ['isProject'];
          } else {
              checkObj = ['isOwner', 'isNotStudent'];
          }
          if (
              checkObj.some(key => conditionObj[key])
          ) {

            // const shouldSetReadOnly = response.projectDetails.evaluationStatus === "Evaluated" || response.projectDetails.fkUserLoginId !== global_var.user_login_id || response.projectDetails.editDisabled;
            // if (
            //   shouldSetReadOnly
            // ) {
            //   setReadOnlyMode(true);
            // }
            setIdeProjectDetails(response.projectDetails);

            if (response.projectDetails.packageList) {
              setSelectedPackagesList(response.projectDetails.packageList)
              if (response.projectDetails.packageList.includes('mysqlConnector')) {
                setShowDatabaseConnectBtn(true)
              }
            }
            // setSubmittedForEvaluation(
            //   response.projectDetails.submittedForEvaluation
            // );
            // setEvaluationStatus(response.projectDetails.evaluationStatus);
            if (response.projectDetails.fileMetaList) {
              setFileMetaList(response.projectDetails.fileMetaList);
              setFileHasContentMap(buildFileContentMap(response.projectDetails.fileMetaList));
            }
            if (
              response.projectDetails.fkResourceId &&
              response.projectDetails.fkActivityMapping &&
              response.projectDetails.totalMark &&
              response.projectDetails.totalMark > 0
            ) {
            }
          } else {
            message.error("you are not authorised");
          }
        } else {
          message.error(response.message);
        }
      });
    } else {
      setIdeProjectDetails(undefined);
      setSelectedFile(undefined);
      setNewProjectModal(true);
      setShowSkulptWindow(false);
      setOutputWindowExpanded(false);
    }
  }, [ideProjectId, isEditAndSaveAllowedInIDE]);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const generateDatabaseConnection = () => {
    generateSqlpadDatabaseConnection().then((response) => {
      response = response.data;
      if (!isCreateDbModalVisible) {
        setIsCreateDbModalVisible(true);
      }
    });
  };

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCreateDbOk = (dbName) => {
    setIsCreateDbModalVisible(false);
    getSqlpadDatabaseConnection().then((response) => {
      response = response.data;
      let connText = `import mysql.connector

conn = mysql.connector.connect(
    host="localhost",
    user="${response[0].username}",
    password="${response[0].password}",
    database="${dbName}"
)
`;


      setConnectionText(connText);

      setIsShowDbConnectionModalVisible(true);
    })
  };

  const handleCreateDbCancel = () => {
    setIsCreateDbModalVisible(false);
  };

  const handleShowDbConnectionCancel = () => {
    setIsShowDbConnectionModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const openMobileFileManagerModal = () => {
    setMobileFileManagerModal(true);
  };

  const closeMobileFileManagerModal = () => {
    setMobileFileManagerModal(false);
  };

  const submittedPackages = (packageList) => {
    installPackageInVnc({
      'packageList': packageList,
      'ideProjectId': ideProjectId
    });
    if (packageList.includes('mysqlConnector')) {
      setShowDatabaseConnectBtn(true)
    }
    setSelectedPackagesList(packageList)
    trackEvent("ide_library_submitted", {
      ide_project_stack_name: "python"
    });
  };

  const handleEditorDidMount = (editor, monaco) => {
    setEditorInstance(editor);
    monacoRef.current = monaco;
    const disablePaste = new URLSearchParams(window.location.search).get("disablePaste");

    if (disablePaste && editor) {
      const noop = () => { };

      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyV, noop);
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyC, noop);

      editor.onDidPaste((event) => {
        // Instead of executeEdits, immediately undo the paste
        editor.trigger('keyboard', 'undo', null);
      });

      const domNode = editor.getDomNode();
      if (domNode) {
        const prevent = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          return false;
        };

        // Block native/system events
        domNode.addEventListener("paste", prevent, true);
        domNode.addEventListener("copy", prevent, true);

        domNode.addEventListener(
          "beforeinput",
          (ev) => {
            if (ev.inputType && ev.inputType.toLowerCase().includes("paste")) {
              prevent(ev);
            }
          },
          true
        );
        domNode.addEventListener("contextmenu", prevent, true);
      }
    }
    if (global_var.role_id !== global_var.STUDENT_ROLE_ID || enabledAIfeatures.includes('ai code generator')) {
      editor.addAction({
        id: "ai-generate",

        label: "Ai generate",
        precondition: null,

        keybindingContext: null,

        contextMenuGroupId: "navigation",

        contextMenuOrder: 1.5,

        run: function () {
          setShowInlineAiPrompt(true)
        },
      });
    }

    if (editorInstance) {
      editorInstance.focus()
    }
  };

  const insertCodeAtLine = (lineNumber, code) => {

    if (editorInstance && cursorPosition && monacoRef.current) {
      const range = new monacoRef.current.Range(lineNumber, 1, lineNumber, 1);
      const model = editorInstance.getModel();
      model.pushStackElement();
      const edit = {
        range: range,
        text: code,
        forceMoveMarkers: true,
      };

      editorInstance.executeEdits('insertCode', [edit]);
      model.pushStackElement();
      const codeLines = code.split('\n');
      const nextLine = (lineNumber + codeLines.length) - 1;
      const position = { lineNumber: nextLine, column: 1 };
      editorInstance.setPosition(position);
      setCursorPosition(position);
      setUnsavedChangePresent(true)
      const updatedCode = model.getValue();
      saveFileInIdeHandler(updatedCode)
    }
  };

  const handlePromptInput = (generatedCode) => {
    if (cursorPosition) {
      insertCodeAtLine(cursorPosition.lineNumber, generatedCode);
    }
  };

  const handleAcceptAiCode = () => {
    setShowInlineAiPrompt(false)
  };

  const handleRejectAiCode = () => {
    if (editorInstance) {
      const model = editorInstance.getModel();
      model.undo();
      setShowInlineAiPrompt(false)
    }
  };

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showInlineAiPrompt) {
        if (editorInstance) {
          const model = editorInstance.getModel();
          model.undo();
          setShowInlineAiPrompt(false)
          let newPosition = editorInstance.getPosition()
          setCursorPosition(newPosition);
        }
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showInlineAiPrompt, editorInstance]);

  const handleChatBotInsert = (codeToInsert) => {
    if (editorInstance) {
      var position = editorInstance.getPosition();
      setCursorPosition(position)
    }
    if (cursorPosition ? cursorPosition.lineNumber : false) {
      insertCodeAtLine(cursorPosition.lineNumber, codeToInsert)
    } else {
      notification.error({
        message: 'Error',
        description: 'Please select the line you wanted to insert the code',
      });

    }
  };

  const handleChatbotVisibility = () => {
    setShowSkulptWindow(true);
    setVncServerUrl(undefined);
    setOutputWindowExpanded(true);
    setSkulptOutputData("");
    setCodeOutputError({});
    trackEvent("ide_ai_chatbot_clicked", {
      ide_project_stack_name: "python",
    });
    setTabActiveKey('Ai Chatbot');
  }

  const handleTabChange = (key) => {
    setTabActiveKey(key);
  };

  const setupWorkerMessageHandler = (worker) => {
    worker.onmessage = (event) => {
      const { type, data } = event.data;

      switch (type) {
        case 'clear':
          setSkulptOutputData('');
          break;
        case 'stdout':
          setSkulptOutputData((prev) => prev + data);
          break;
        case 'error':
          setCodeExecutionInProgress(false);
          setCodeOutputError({ error: data });
          break;
        case 'done':
          setCodeExecutionInProgress(false);
          break;
        case 'input_prompt':
          setSkulptOutputData((prev) => prev + data);
          setShowSkulptInputField(true);
          break;
        default:
          break;
      }
    };
  };

  useEffect(() => {

    if (global_var.allowedAiFeatures) {
      setEnabledAIfeatures(global_var.allowedAiFeatures);
    }

    // Function to initialize the worker and its message handler
    const initWorker = () => {
      // The new URL() syntax is the standard way to load workers with modern bundlers like Webpack
      const worker = new Worker(new URL('../../workers/python-skulpt.worker.js', import.meta.url));
      workerRef.current = worker;
      setupWorkerMessageHandler(worker);
    };

    initWorker();

    // Cleanup function: terminate the worker when the component unmounts to prevent memory leaks
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };

  }, []);

  const stopCodeExecution = () => {
    if (workerRef.current) {
      // 1. Immediately terminate the running worker
      workerRef.current.terminate();

      // 2. Create a new worker instance to be ready for the next run
      const newWorker = new Worker(new URL('../../workers/python-skulpt.worker.js', import.meta.url));
      workerRef.current = newWorker;

      // 3. Re-setup the message handler for the new worker
      setupWorkerMessageHandler(newWorker);

      // 4. Update the UI
      setSkulptOutputData((prev) => prev + "\n--- Execution stopped by user ---\n");
      setShowSkulptInputField(false); // Hide input field if it was open
      setCodeExecutionInProgress(false);
    }
  };

  const handleSkulptInput = (value) => {
    if (workerRef.current) {
      // Add the user's input to the visible output
      setSkulptOutputData((prev) => prev + value + '\n');

      // Send the input value back to the waiting worker
      workerRef.current.postMessage({ type: 'input_response', data: value });

      // Hide the input field and clear the state
      setShowSkulptInputField(false);
      setSkulptInputData('');
    }
  };

  return (
    <div className="container-fluid p-0">
      <div className="row mt-1">
        <div className="col">
          {ideProjectDetails && hideHeader !== "true" && (
            <IdeHeader
              headerName="CS Python IDE"
              showInPortFolio={ideProjectDetails.showInPortFolio}
              submittedForEvaluation={ideProjectDetails.submittedForEvaluation}
              // setSubmittedForEvaluation={setSubmittedForEvaluation}
              ideProjectDetails={ideProjectDetails}
              fkResourceId={ideProjectDetails.fkResourceId}
              unsavedChangePresent={unsavedChangePresent}
              readOnlyMode={readOnlyMode}
              onUpdateProjectDetails={updateProjectDetails}
              isProjectEmpty={isProjectEmptyFromMap(fileHasContentMap)}
            />
          )}

          <div className="row mt-2 rounded-top-left pt-2 bg-white">
            {ideProjectDetails ? (
              <div
                className={getCssClassName("file-manager")}
                style={{ height: "85vh" }}
              >
                <FileManager
                  fileStructure={fileMetaList}
                  defaultSelectedKey={ideProjectDetails.lastAccessedFileId}
                  ideProjectId={ideProjectId}
                  ideProjectDetails={ideProjectDetails}
                  nodeSelected={nodeSelected}
                  handleNewFile={handleNewFile}
                  handleFileDeleted={handleFileDeleted}
                  handleFileRenamed={handleFileRenamed}
                  readOnlyMode={readOnlyMode}
                  projectStack="python"
                  uploadAcceptFileTypes=".py, .csv, .json, .txt, .png, .jpg, .jpeg"
                />
              </div>
            ) : (
              false
            )}

            {fileLoadingInProgress && (
              <div className="col-10 text-center mt-3">
                <Skeleton active="true" paragraph={{ rows: 8 }} />
              </div>
            )}

            {selectedFile ? (
              <div className={getCssClassName("code-editor-output-container")}>
                <div className="row mb-1">
                  <div className={getCssClassName("code-editor")}>
                    <div className="row mb-1">
                      <div className="col">
                        <Space direction="vertical">
                          <Space wrap>
                            {isMobile ? (
                              <OptionIcon
                                onClick={() => openMobileFileManagerModal()}
                                style={{ cursor: 'pointer' }}
                              />
                            ) : fileManagerExpanded ? (
                              <ExpandIcon
                                onClick={() => setFileManagerExpanded(false)}
                                style={{ cursor: 'pointer' }}
                              />
                            ) : (
                              <CollapseIcon
                                onClick={() => setFileManagerExpanded(true)}
                                style={{ cursor: 'pointer' }}
                              />
                            )}

                            <span className="badge" style={{ backgroundColor: '#404040' }}>
                              {selectedFile.fileName}
                            </span>

                            {unsavedChangePresent && (
                              <Tooltip
                                placement="topLeft"
                                title="There are unsaved changes in your editor"
                              >
                                <Badge status="error" />
                              </Tooltip>
                            )}
                          </Space>
                        </Space>

                        {selectedFile.fileMimeType !== "image" &&
                          !fileLoadingInProgress ? (
                          <Space direction="vertical" className="float-end">
                            <Space wrap>

                              {
                                selectedPackagesList.map((item, index) => (
                                  // <span key={index}>sds</span>
                                  // If you want to use the commented-out Badge component:
                                  <Badge key={index} status="success" text={item} style={{ backgroundColor: '#fff', color: '#404040', boxShadow: '0 0 0 1px #d9d9d9 inset', borderRadius: '5px', padding: '4px' }} />
                                ))
                              }


                              <PluginsLoader
                                selectedPackagesList={selectedPackagesList}
                                submittedPackages={submittedPackages}
                                ideProjectStackName='python'
                              />

                              {showDatabaseConnectBtn && (
                                <Button
                                  style={{
                                    backgroundColor: 'white',
                                    color: 'black',
                                    border: '1px solid #eeeeee',
                                    height: '32px',
                                    borderRadius: '5px'
                                  }}
                                  onClick={generateDatabaseConnection}
                                  loading={false}
                                >
                                  <DatabaseOutlined />Generate Database Connection
                                </Button>
                              )}

                              <Button
                                style={{
                                  backgroundColor: 'white',
                                  color: 'black',
                                  border: '1px solid #eeeeee',
                                  height: '32px',
                                  borderRadius: '5px'
                                }}
                                onClick={showModal}
                                loading={false}
                              >
                                <ComplexityIcon />Analyse complexity
                              </Button>
                              {(global_var.role_id !== global_var.STUDENT_ROLE_ID || enabledAIfeatures.includes('Ai chatbot')) &&
                                <Button
                                  style={{
                                    backgroundColor: 'white',
                                    color: 'black',
                                    border: '1px solid #eeeeee',
                                    height: '32px',
                                    borderRadius: '5px'
                                  }}
                                  onClick={handleChatbotVisibility}
                                >
                                  <ComplexityIcon />Ai chatbot
                                </Button>

                              }

                              {(!readOnlyMode && !isSaveDisable) && (

                                <Button
                                  style={{ backgroundColor: '#4235A1' }}
                                  loading={fileSavingInProgress}
                                  className="float-end"
                                  type="primary"
                                  onClick={handleSaveFile}
                                  size="small"
                                >
                                  Save
                                </Button>
                              )}

                              <Button
                                style={{ backgroundColor: '#4235A1' }}
                                loading={vncRunInProgress || codeExecutionInProgress}
                                className="float-end"
                                type="primary"
                                onClick={handleRunCode}
                                size="small"
                              >
                                Run
                              </Button>

                              {codeExecutionInProgress && (
                                <Button
                                  danger
                                  type="primary"
                                  className="float-end"
                                  onClick={stopCodeExecution}
                                  size="small"
                                >
                                  Stop
                                </Button>
                              )}

                              <Button className="float-end"
                                style={{ backgroundColor: '#FFC200' }}
                                type="primary"
                                size="small"
                                onClick={handleSelectedTheme}>
                                {selectedTheme === 'light' ? <BulbIcon style={{ fontSize: '16px' }} /> : <BulbIcon />}
                              </Button>

                              {(vncServerUrl || showSkulptWindow) &&
                                (outputWindowExpanded ? (
                                  <FullscreenExitOutlined
                                    onClick={toggleOutputWindow}
                                    style={{ fontSize: "1.5rem" }}
                                  />
                                ) : (
                                  <FullscreenOutlined
                                    onClick={toggleOutputWindow}
                                    style={{ fontSize: "1.5rem" }}
                                  />
                                ))}
                            </Space>
                          </Space>
                        ) : (
                          false
                        )}
                      </div>
                    </div>

                    <div className="row mb-1">
                      {selectedFile.fileMimeType === "image" ? (
                        <div className="col text-center">
                          <img
                            alt={selectedFile.fileName}
                            className="img-thumbnail img-fluid"
                            src={selectedFile.fileS3reference}
                          ></img>
                        </div>
                      ) : (
                        <div className="col p-0" style={{ overflowX: "auto", position: "relative" }}>
                          <Editor
                            height={isMobile ? "45vh" : "80vh"}
                            onChange={handleCodeChange}
                            defaultLanguage={
                              selectedFile.fileMimeType
                                ? selectedFile.fileMimeType
                                : "plaintext"
                            }
                            theme={selectedTheme}
                            defaultValue="# Enter your code here..."
                            value={
                              selectedFile.fileContent
                                ? selectedFile.fileContent
                                : ""
                            }
                            onMount={handleEditorDidMount}
                            options={{ readOnly: readOnlyMode }}
                          />

                          {
                            showInlineAiPrompt &&
                            <div
                              style={{
                                position: "absolute",
                                top: `calc(50% - 250px)`,
                                left: `calc(50% - 300px)`,
                                zIndex: 1000,
                              }}
                            >
                              <InlineAiPrompt
                                ProjectLanguage="Python"
                                cursorPosition={cursorPosition}
                                handlePrompt={handlePromptInput}
                                handleCodeAcceptHandler={handleAcceptAiCode}
                                handleRejectHandler={handleRejectAiCode}
                                selectedTheme={selectedTheme}
                              />
                            </div>
                          }

                        </div>
                      )}
                    </div>
                  </div>

                  <div className={getCssClassName("output-window")}>
                    <div className="row">
                      {/* <div className="col p-0 fw-medium">Output</div> */}
                      <div className="pv-3">
                        <Tabs
                          type="card"
                          activeKey={tabActiveKey}
                          onChange={handleTabChange}
                          items={tabs.map((_, i) => {
                            const id = tabs[i];
                            return {
                              label: id,
                              key: id,
                              children: (
                                <>
                                  {id === 'Output' &&
                                    <div className="row">
                                      {vncServerUrl && !vncRunInProgress && !vncRestartInProgress && (
                                        <div className="col border rounded p-1">
                                          <iframe
                                            id="pythonIframe"
                                            title={ideProjectId}
                                            src={vncServerUrl}
                                            width="100%"
                                            style={{ minHeight: 500, maxHeight: 500 }}
                                          ></iframe>
                                        </div>
                                      )}

                                      {showSkulptWindow && !vncRunInProgress && (
                                        <div
                                          ref={outputRef}
                                          className="col border rounded p-3 text-bg-dark"
                                          style={{
                                            height: 605,
                                            overflowY: "auto",
                                            overflowX: "auto",
                                          }}
                                        >
                                          <pre style={{ overflow: null }}>
                                            {skulptOutputData}
                                            {showSkulptInputField &&
                                              <input type="text" autoFocus value={skulptInputData} onChange={e => {
                                                setSkulptInputData(e.target.value)
                                              }} onKeyUp={e => {
                                                if (e.key === 'Enter') {
                                                  handleSkulptInput(skulptInputData);
                                                }
                                              }} style={{
                                                borderRadius: '5px',
                                                border: 'none',
                                                outline: 'none'
                                              }} />
                                            }
                                          </pre>
                                          {codeOutputError.error && (
                                            <Alert
                                              message={codeOutputError.errorName}
                                              description={codeOutputError.error}
                                              type="error"
                                              showIcon
                                            />
                                          )}
                                        </div>
                                      )}

                                      {(vncRunInProgress || vncRestartInProgress) ? (
                                        <div
                                          className="col border rounded p-1 bg-light-subtle"
                                          style={{ minHeight: 500 }}
                                        >
                                          <div className="col mt-4 h5 text-center">
                                            Your machine is booting{" "}
                                            <Spin
                                              indicator={
                                                <LoadingOutlined
                                                  style={{ fontSize: 24 }}
                                                  spin
                                                />
                                              }
                                            />
                                          </div>
                                          <div className="col mt-2">
                                            <Progress percent={machineBootingProgress} />
                                          </div>
                                          <div className="col text-center fw-light small">
                                            The machine may take between 40 to 60 seconds to
                                            boot.
                                          </div>

                                          {programmingTip ? (
                                            <div className="col p-2 mt-3 border border-2 rounded">
                                              <div className="col-12 fw-bold d-flex align-items-center justify-content-center">
                                                <BulbOutlined /> Programming Tip
                                              </div>
                                              <div className="col-12">
                                                <ReactMarkdown children={programmingTip} />
                                              </div>
                                            </div>
                                          ) : (
                                            false
                                          )}
                                        </div>
                                      ) : (
                                        false
                                      )}
                                    </div>
                                  }
                                  {id === 'Ai Chatbot' && (global_var.role_id !== global_var.STUDENT_ROLE_ID || enabledAIfeatures.includes('Ai chatbot')) &&
                                    <AiChatBot
                                      fileMetaList={fileMetaList}
                                      ideProjectId={ideProjectId}
                                      selectedFileId={selectedFile ? selectedFile : false}
                                      selectedTheme={selectedTheme}
                                      handleChatBotInsert={handleChatBotInsert}
                                    />
                                  }

                                </>
                              ),
                            };
                          })}
                        />
                      </div>
                      {vncServerUrl && (
                        <div className="col text-end fw-medium">
                          <Button
                            type="primary"
                            loading={vncRestartInProgress}
                            onClick={handleVncRestartClicked}
                            size="small"
                          >
                            Restart
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              false
            )}
          </div>
        </div>
        <ComplexityModal
          isModalVisible={isModalVisible}
          handleOk={handleOk}
          handleCancel={handleCancel}
          ideProjectId={ideProjectId}
          selectedFileId={selectedFile ? selectedFile : false}
          ideProjectStackName='python'
        />
        <CreateDbModal
          isModalVisible={isCreateDbModalVisible}
          handleOk={handleCreateDbOk}
          handleCancel={handleCreateDbCancel}
        />

        <ShowDbConnection
          isModalVisible={isShowDbConnectionModalVisible}
          handleOk={handleShowDbConnectionCancel}
          handleCancel={handleShowDbConnectionCancel}
          connectionText={connectionText}
          theme={selectedTheme}
        />

        <Modal
          open={mobileFileManagerModal}
          onCancel={closeMobileFileManagerModal}
          footer={null}
          width={'60%'}
          bodyStyle={{ height: '90vh', overflow: 'auto', padding: '10px' }}
          style={{ marginLeft: '0px', marginTop: '-90px' }}
        >
          {ideProjectDetails && (
            <FileManager
              fileStructure={fileMetaList}
              defaultSelectedKey={ideProjectDetails.lastAccessedFileId}
              ideProjectId={ideProjectId}
              ideProjectDetails={ideProjectDetails}
              nodeSelected={nodeSelected}
              handleNewFile={handleNewFile}
              handleFileDeleted={handleFileDeleted}
              handleFileRenamed={handleFileRenamed}
              readOnlyMode={readOnlyMode}
              projectStack="python"
            />
          )}
        </Modal>

        {newProjectModal && <CreateIDEProjectModal projectStack="python" />}
      </div>
    </div>
  );
};

export default PythonIDE;
